import React from "react";
import { get } from "./api";
import { captureError } from "./errorHandler";

const CACHE_KEY = "supportedFileExtensions";
const CACHE_TTL_KEY = "supportedFileExtensionsTTL";
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

let inFlightPromise = null;

/**
 * Read cached extensions from localStorage if still within TTL.
 * Returns the array or null.
 */
const readCache = () => {
  try {
    const ttl = localStorage.getItem(CACHE_TTL_KEY);
    if (!ttl || Date.now() > Number(ttl)) return null;

    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const writeCache = (extensions) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(extensions));
    localStorage.setItem(CACHE_TTL_KEY, String(Date.now() + CACHE_TTL_MS));
  } catch {
    // localStorage full or unavailable — non-critical
  }
};

/**
 * Fetch supported file extensions from the backend.
 * Uses a singleton promise so concurrent callers share the same request.
 * Results are cached in localStorage with a 1-hour TTL.
 *
 * Returns the extensions array, or null on failure (graceful degradation —
 * callers should skip validation when null).
 */
export const fetchSupportedExtensions = () => {
  const cached = readCache();
  if (cached) return Promise.resolve(cached);

  if (inFlightPromise) return inFlightPromise;

  inFlightPromise = get("/supported-file-extensions/")
    .then((res) => {
      const extensions = res.data?.supported_extensions;
      if (Array.isArray(extensions)) {
        writeCache(extensions);
        inFlightPromise = null;
        return extensions;
      }
      inFlightPromise = null;
      return null;
    })
    .catch((error) => {
      captureError(error, "fetching supported file extensions");
      inFlightPromise = null;
      return null;
    });

  return inFlightPromise;
};

/**
 * Get the file extension (lowercased, with leading dot) from a filename.
 */
const getFileExtension = (filename) => {
  if (!filename) return "";
  const lastDot = filename.lastIndexOf(".");
  if (lastDot === -1 || lastDot === filename.length - 1) return "";
  return filename.slice(lastDot).toLowerCase();
};

/**
 * Validate files against the supported extensions list.
 * Waits for the extensions to be fetched if a request is in-flight,
 * or triggers a fetch if none has been started.
 *
 * Returns { validFiles, unsupportedFiles } where unsupportedFiles
 * contains { file, extension } entries.
 * When the extension list is unavailable, all files are treated as valid
 * (graceful degradation — let the backend decide).
 */
export const filterUnsupportedFiles = async (files) => {
  const extensions = await fetchSupportedExtensions();

  if (!extensions) {
    return { validFiles: Array.from(files), unsupportedFiles: [] };
  }

  const extensionSet = new Set(extensions.map((e) => e.toLowerCase()));

  const validFiles = [];
  const unsupportedFiles = [];

  for (const file of files) {
    const ext = getFileExtension(file.name);
    if (!ext || extensionSet.has(ext)) {
      validFiles.push(file);
    } else {
      unsupportedFiles.push({ file, extension: ext });
    }
  }

  return { validFiles, unsupportedFiles };
};

/**
 * Build a human-readable string of supported extensions for error messages.
 * e.g. ".csv, .jpg, .pdf, .png, .xls, .xlsx, .heic"
 */
export const formatSupportedExtensions = (extensions) => {
  if (!extensions || extensions.length === 0) return "";
  return extensions
    .map((e) => (e.startsWith(".") ? e : `.${e}`).toLowerCase())
    .join(", ");
};

/**
 * Fire-and-forget: hit the backend upload-url endpoint with each unsupported
 * filename so the backend can log which extensions users are attempting.
 * We expect 400 responses — they are intentionally silenced.
 */
export const reportUnsupportedFiles = (unsupportedFiles) => {
  for (const { file } of unsupportedFiles) {
    get(`/onboarding/upload-url/?filename=${encodeURIComponent(file.name)}&action=upload`)
      .catch(() => {});
  }
};

/**
 * Build a JSX toast message for unsupported file(s), with supported formats on a new line.
 */
export const buildUnsupportedFileMessage = (unsupportedFiles, extensions) => {
  const names =
    unsupportedFiles.length <= 2
      ? unsupportedFiles.map((u) => u.file.name).join(", ")
      : `${unsupportedFiles
          .slice(0, 2)
          .map((u) => u.file.name)
          .join(", ")} +${unsupportedFiles.length - 2} more`;

  const supported = formatSupportedExtensions(extensions);
  return (
    <span style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
      <span>
        Unsupported file type{unsupportedFiles.length > 1 ? "s" : ""} skipped: {names}.
        <br />
        Supported formats: {supported}
      </span>
      <span style={{ cursor: "pointer", fontWeight: "bold", fontSize: "16px", lineHeight: "1", flexShrink: 0, opacity: 0.7 }}>✕</span>
    </span>
  );
};
